<template>
  <section v-bind="sectionProps">
    <v-container v-bind="containerProps">
      <v-row v-bind="contentRowProps">
        <v-col v-bind="contentColumnProps">
          <v-row v-bind="contentInnerRowProps">
            <v-col v-bind="mapColumnProps" v-if="!builder">
              <iframe v-bind="mapProps"></iframe>
            </v-col>

            <v-col v-else>
              <v-img
                :height="this.section.layout['map-height'] || 450"
                src="@/assets/images/map_placeholder.jpg"
              ></v-img>
            </v-col>

            <v-col v-bind="textColumnProps">
              <div
                class="section-title"
                v-bind="titleProps"
                v-if="section.content.title"
                v-inline-editable.html="section.content.title"
              ></div>

              <div
                class="section-introtext"
                v-bind="introtextProps"
                v-if="section.content.introtext"
                v-inline-editable.html="section.content.introtext"
              ></div>

              <div
                v-if="parseBoolean(section.layout['content-box'])"
                v-bind="contentBoxProps"
              ></div>

              <content-buttons :buttons="section.content.buttons" />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>

    <parallax-background-image
      v-if="section.design['background-image']"
      :section="section"
      v-bind="backgroundImageProps"
    ></parallax-background-image>

    <slot name="toolbar"></slot>
  </section>
</template>

<script>
import ContentButtons from '../../partials/buttons/ContentButtons'
import ParallaxBackgroundImage from '@/components/sections/partials/elements/ParallaxBackgroundImage'

export default {
  name: 'MapDefaultSection',
  props: {
    section: {
      type: Object,
      required: true
    },
    templateSettings: {
      type: Object,
      required: true
    },
    builder: {
      type: Boolean,
      default: true
    }
  },
  components: {
    ContentButtons,
    ParallaxBackgroundImage
  },
  data: () => ({
    mapApiKey: process.env.VUE_APP_GOOGLE_MAPS_API_KEY
  }),
  computed: {
    mapEmbedUrl() {
      let lat = this.section.content['map-latitude'] || '59.921167'
      let long = this.section.content['map-longitude'] || '10.757151'
      let zoom = this.section.content['map-zoom'] || '15'

      return `https://www.google.com/maps/embed/v1/place?key=${this.mapApiKey}&q=${lat},${long}&zoom=${zoom}`
    },
    mapProps() {
      return {
        width: '100%',
        height: this.section.layout['map-height'] || 450,
        style: {
          border: 0,
          marginBottom: '-5px'
        },
        loading: 'lazy',
        allowfullscreen: true,
        src: this.mapEmbedUrl
      }
    },
    sectionProps() {
      let additionalStyle = {}

      if (this.parseBoolean(this.section.design['custom-padding'])) {
        additionalStyle.paddingTop = this.section.design['padding-top']
        additionalStyle.paddingBottom = this.section.design['padding-bottom']
      }

      return {
        style: {
          backgroundColor: this.section.design['background-color'],
          ...additionalStyle
        }
      }
    },
    backgroundImageProps() {
      return {
        src: this.loadMedia(this.section.design['background-image'], 'xl'),
        'lazy-src': this.loadMedia(
          this.section.design['background-image'],
          'thumbnail'
        ),
        style: {
          height: '100%',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0
        }
      }
    },
    containerProps() {
      return {
        'fill-height': true,
        fluid: this.parseBoolean(
          this.section.layout['content-container-fluid']
        ),
        style: {
          position: 'relative',
          zIndex: 1
        }
      }
    },
    contentRowProps() {
      return {
        'no-gutters': true,
        class: [
          `justify-${this.section.layout['content-horizontal-alignment']}`,
          `text-${this.section.layout['content-justify']}`
        ]
      }
    },
    contentColumnProps() {
      let additionalProps = {}

      if (!this.parseBoolean(this.section.layout['content-container-fluid'])) {
        additionalProps = {
          ...this.calculateColumnContainerSize(
            this.section.layout['content-container-size']
          )
        }
      }

      return {
        ...additionalProps,
        style: {
          position: 'relative',
          padding: this.parseBoolean(this.section.layout['content-box'])
            ? this.section.layout['content-box-padding']
            : 0
        }
      }
    },
    contentInnerRowProps() {
      return {
        class: [`align-${this.section.layout['content-vertical-alignment']}`]
      }
    },
    mapColumnProps() {
      return {
        ...this.calculateImageColumns(this.section.layout['map-size']),
        'order-md': this.section.layout['map-position'] == 'left' ? 0 : 1,
        class: ['py-0']
      }
    },
    textColumnProps() {
      return {
        ...this.calculateTextColumns(this.section.layout['map-size'])
      }
    },
    titleProps() {
      return {
        is: this.section.design['title-tag'],
        style: {
          color: this.section.design['title-color'],
          fontWeight: this.section.design['title-font-weight']
        }
      }
    },
    introtextProps() {
      return {
        style: {
          color: this.section.design['introtext-color']
        }
      }
    },
    contentBoxProps() {
      let additionalClass = []

      if (this.parseBoolean(this.section.layout['content-box-rounded'])) {
        additionalClass.push('rounded')
      }

      return {
        class: [
          `elevation-${this.section.layout['content-box-elevation']}`,
          ...additionalClass
        ],
        style: {
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: -1,
          backgroundColor: this.section.layout['content-box-color'],
          opacity: this.section.layout['content-box-opacity']
        }
      }
    }
  },
  methods: {
    calculateImageColumns(mapSize) {
      switch (mapSize) {
        case 'lg':
          return {
            md: 7
          }
        case 'md':
          return {
            md: 6
          }
        case 'sm':
          return {
            md: 5
          }
        default:
          return {
            md: 6
          }
      }
    },
    calculateTextColumns(mapSize) {
      switch (mapSize) {
        case 'lg':
          return {
            md: 5
          }
        case 'md':
          return {
            md: 6
          }
        case 'sm':
          return {
            md: 7
          }
        default:
          return {
            md: 6
          }
      }
    },
    buttonProps(button) {
      return {
        class: ['mr-3', 'mb-3'],
        depressed: true,
        outlined: button['button-style'] == 'outlined',
        text: button['button-style'] == 'text',
        color:
          button['button-style'] == 'text'
            ? 'transparent'
            : button['button-background-color'],
        style: {
          color: button['button-text-color'],
          borderColor: button['button-background-color']
        }
      }
    }
  }
}
</script>
